import React, { useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import Button from '../ui/Button';
import DataTable from '../ui/DataTable';
import Avatar from '../ui/Avatar';
import DirectorPanelState from './DirectorPanelState';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../../utils/directorEvents';
import { formatTimestamp } from '../../utils/formatDate';
import { buildQuery } from '../../utils/buildQuery';

const ENTITY_OPTIONS = [
  { value: '', label: 'All entities' },
  { value: 'location', label: 'Locations' },
  { value: 'manager', label: 'Managers' },
  { value: 'user', label: 'Users' },
  { value: 'goal', label: 'Goals' },
  { value: 'kpi', label: 'KPIs' },
  { value: 'policy', label: 'Policies' },
  { value: 'event', label: 'Requests' }
];

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'activate', label: 'Activate' },
  { value: 'deactivate', label: 'Deactivate' },
  { value: 'reactivate', label: 'Reactivate' },
  { value: 'reset_password', label: 'Reset Password' },
  { value: 'batch_create', label: 'Batch Create' },
  { value: 'allocate', label: 'Allocate' },
  { value: 'lock', label: 'Lock' },
  { value: 'unlock', label: 'Unlock' },
  { value: 'approve', label: 'Approve' },
  { value: 'deny', label: 'Deny' },
  { value: 'record', label: 'Record' },
  { value: 'manual_entry', label: 'Manual Entry' }
];

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function DirectorAuditPanel({ selectedLocationCode = ALL_DIRECTOR_LOCATIONS, locationLabel = 'Company Rollup' }) {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');

  const auditUrl = useMemo(() => {
    const qs = buildQuery({
      limit: 150,
      entityType,
      action,
      search: search.trim(),
      locationCode: selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null
    });
    return `/api/audit/director-actions?${qs}`;
  }, [action, entityType, search, selectedLocationCode]);

  const { data: fetchedLogs, isLoading, error, refetch } = useFetch(auditUrl, { initialData: [] });
  const logs = useMemo(
    () => (Array.isArray(fetchedLogs) ? fetchedLogs : []),
    [fetchedLogs]
  );

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  const summary = useMemo(() => ({
    total: logs.length,
    latestActor: logs[0]?.actorEmail || 'Director',
    latestAction: logs[0]?.summary || 'No actions yet'
  }), [logs]);

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading audit trail...' : ''} />
      <DirectorPanelState kind="error" message={error} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Director Audit Trail</h2>
            <span>{summary.total} entries</span>
          </div>
          <p className="role-section-copy">
            Review director actions for {locationLabel}. Logs are append-only and newest first.
          </p>
          <div className="director-toolbar">
            <div className="director-toolbar-group">
              <label htmlFor="audit-entity">Entity</label>
              <select id="audit-entity" value={entityType} onChange={(event) => setEntityType(event.target.value)}>
                {ENTITY_OPTIONS.map(option => (
                  <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="director-toolbar-group">
              <label htmlFor="audit-action">Action</label>
              <select id="audit-action" value={action} onChange={(event) => setAction(event.target.value)}>
                {ACTION_OPTIONS.map(option => (
                  <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="director-toolbar-group director-toolbar-search">
              <label htmlFor="audit-search">Search</label>
              <input
                id="audit-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Actor, entity, summary..."
              />
            </div>
            <div className="director-toolbar-group">
              <label>Quick Filters</label>
              <div className="role-section-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEntityType('event');
                    setAction('record');
                  }}
                >
                  Attendance Records
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEntityType('event');
                    setAction('manual_entry');
                  }}
                >
                  Manual PTO
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEntityType('');
                    setAction('');
                    setSearch('');
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Audit Log</h2>
          <span>{logs.length} shown</span>
        </div>
        <DataTable
          tableClassName="director-audit-table"
          emptyMessage="No audit actions match the current filters yet."
          isEmpty={!logs.length}
          loading={isLoading}
          loadingRowCount={5}
          columns={[
            { label: 'Time', className: 'director-audit-col-time' },
            { label: 'Actor', className: 'director-audit-col-actor' },
            { label: 'Action', className: 'director-audit-col-action' },
            { label: 'Entity', className: 'director-audit-col-entity' },
            { label: 'Summary' }
          ]}
        >
          {logs.map(log => (
            <tr key={log.id}>
              <td className="director-audit-col-time">{formatTimestamp(log.createdAt)}</td>
              <td className="director-audit-col-actor">
                <span className="director-person-cell">
                  <Avatar
                    email={log.actorEmail}
                    identifier={log.actorUserId || log.actorEmail}
                    name={log.actorEmail}
                    size="xs"
                    userId={log.actorUserId}
                  />
                  <span className="full-text-value">{log.actorEmail}</span>
                </span>
              </td>
              <td className="director-audit-col-action">{String(log.action || '').replace(/_/g, ' ')}</td>
              <td className="director-audit-col-entity">{log.entityLabel || log.entityType}</td>
              <td>{log.summary}</td>
            </tr>
          ))}
        </DataTable>
      </section>
    </div>
  );
}

export default DirectorAuditPanel;
